/**
 * R7 WS-1 §3 E13 — `GET /v1/admin/billing/summary`.
 *
 * Producer `site-admin-api/src/modules/billing-admin/billing-admin.controller.ts:57-58`,
 * `cp:billing:read` · **site**, `/v1/admin` → site-admin-api (`httproute.yaml:213`).
 *
 * # ⛔ THE CONTRACT IS IMPORTED, NEVER HAND-DECLARED
 *
 * `CostSummary` is a published zod schema — `costSummarySchema`
 * (`cloud/platform-sdk/contracts/src/billing.ts:229-241`), exported from `@zion/sdk-contracts`
 * (`index.ts:955`) — and the controller's own return type. Importing it means a producer change
 * becomes a typecheck failure here instead of a silent runtime mismatch. (Contrast
 * `tenants.api.ts`, which hand-declares: those shapes genuinely have no contract entry.)
 *
 * # Four facts from the schema that decide the panel
 *
 * 1. **`byDataPlane: { key: dpId, cost }[]` ⇒ ONE unfiltered call for the whole screen**, joined to
 *    each tenant through its placement dpId. ⛔ Not one call per org: the endpoint *accepts* `orgId`
 *    (`billing-admin.controller.ts:62`) but using it would be N+1 for data that is already bucketed.
 * 2. **`cost` is a DECIMAL STRING** (`billingDecimalStringSchema`). ⛔ No `parseFloat`, no
 *    arithmetic, no summing in the client. Render it as given, with `currency`. A11 asserts the
 *    exact string (`'1120.00'` renders as `1120.00`, not `1120`) — that is what pins "no arithmetic".
 * 3. **`grandTotalCost` (rollup rows) and `scaleEventCost` (scale-event rows) are NEVER combined**
 *    (D20.4, stated in the schema's own docblock `billing.ts:225-229`) ⇒ two distinct lines, never
 *    one total.
 * 4. **"no rate card" = the tenant's dpId is ABSENT from `byDataPlane`** ⇒ `NotCollectedState` with
 *    the reason *"the absence of a price, not a price of zero"*. ⛔ Never `$0`.
 *
 * ⚠️ **DEFERRED with the lead's sign-off** (WS-1 §8.1): screen 11's *Prior period* and
 * *Est. remaining* tiles. Comparing two periods means decimal arithmetic plus a precision rule, and
 * this workstream will not invent one for money. The Spend card ships **two** tiles —
 * *This period* (`grandTotalCost`) and *Autoscale events* (`scaleEventCost`).
 */
import type {
  BillingAppOption,
  BillingLedgerPage,
  BillingTrendGroupBy,
  CostLedgerKind,
  CostLedgerLine,
  CostLedgerScope,
  CostReconciliationEntry,
  CostReconciliationGranularity,
  CostSummary,
  CostSummaryBucket,
  CostTrend,
  PricingRateEntry,
  ScaleEventWithCost,
} from '@zion/sdk-contracts';
import { api } from '../platform/http/client';

export type { CostSummary, CostSummaryBucket };

export const BILLING_READ_PERMISSION = 'cp:billing:read';

/** The base path. ⛔ Never requested bare — see {@link billingSummaryPath}. */
export const BILLING_SUMMARY_PATH = '/v1/admin/billing/summary';

/**
 * # ⛔ `startDate` AND `endDate` ARE REQUIRED. A BARE REQUEST IS A 400.
 *
 * `billingLedgerFiltersSchema` declares `startDate: z.string().datetime()` and `endDate` with **no
 * `.optional()`** (`contracts/src/billing.ts:270-271`), and its own comment states the intent: *"D27:
 * startDate/endDate REQUIRED (bounded queries — avoids unbounded scans); every caller always sends
 * them."* `ZionStrictValidationPipe` is wired globally, so the omission is rejected at the boundary.
 *
 * ⚠️ **The controller's `@ApiQuery({ required: false })` on both says the opposite, and the SCHEMA is
 * what runs.** WS-1 §3.1.1 was written off that decorator and specified "one **unfiltered** call" —
 * which would have 400'd on every load of screen 10. Measured, not inferred: an empty query fails
 * `safeParse` with `startDate: invalid_type | endDate: invalid_type`.
 *
 * ⚠️ And the failure would not have been loud even if validation were bypassed: `buildLedgerFilters`
 * applies both dates unconditionally (`billing-admin.service.ts:454-458`), so `new Date(undefined)`
 * ⇒ `Invalid Date` ⇒ zero rows ⇒ `grandTotalCost: '0'` — a confident zero in the money column
 * meaning *we sent a broken query*.
 *
 * ⛔ **An MSW test cannot catch this**: a fake answers whatever its handler says regardless of the
 * query string. The path assertion in `tenants-api-paths.test.ts` is the only thing standing between
 * this module and that defect class, which is why it pins the **literal** query string.
 */
export interface BillingWindow {
  /** ISO-8601 with offset. */
  readonly startDate: string;
  readonly endDate: string;
}

/**
 * The reporting period: **calendar month to date** (lead ruling ACK-ws1-5).
 *
 * `startDate` = the first of the current month at `00:00Z`; `endDate` = now. Chosen because it is the
 * window an invoice describes, so the figure on screen and the figure the customer is billed cover
 * the same period.
 *
 * ⚠️ `nowMs` is INJECTED, for two reasons. It makes the derivation testable without freezing a clock —
 * and more importantly, the caller must hold the result in state: this value is part of a
 * `useApiResource` key, and recomputing `Date.now()` at render time would produce a new key on every
 * render and **refetch forever**. `AppHistoryPane` hit exactly that and solved it the same way.
 *
 * ⛔ UTC month boundaries via `Date.UTC`, never `new Date(y, m, 1)` — the local-time form puts the
 * boundary hours off in either direction depending on the operator's zone, which silently moves money
 * between two months' figures for anyone outside UTC.
 */
export function monthToDate(nowMs: number): BillingWindow {
  const now = new Date(nowMs);
  return {
    startDate: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString(),
    endDate: now.toISOString(),
  };
}

/**
 * ⭐ **The card's label, and it names the WINDOW.** Lead ruling ACK-ws1-5: picking a period fixes half
 * the defect; *saying which period* fixes the rest. ⛔ Not "This period" — a vague label is how a
 * month-to-date figure gets read as a monthly total by someone holding an invoice.
 */
export const SPEND_WINDOW_LABEL = 'This month to date';

/**
 * ⛔ Scale-event cost is `kind='scale_event'` ONLY and `grandTotalCost` is `kind='rollup'` ONLY; D20.4
 * forbids combining them, so they are two labelled lines and never one total.
 */
export const SPEND_SCALE_LABEL = 'Autoscale events';

export const SPEND_METERED_NOTE =
  'Metered infrastructure only. Compute is billed whether the workload succeeds or not.';

/**
 * Build the path.
 *
 * @param dataPlaneId scope the WHOLE response to one data plane. ⛔ **Screen 11 MUST pass this and
 * screen 10 MUST NOT** (lead ruling ACK-ws1-5). `byDataPlane` is built from `rollupBase` alone while
 * `scaleEventCost` is a separate **fleet-wide** total with no per-DP breakdown anywhere in
 * `CostSummary` — so on a single-tenant page an unfiltered response would attribute a fleet total to
 * that one tenant. `buildLedgerFilters` applies `dataPlaneId` to **both** bases
 * (`billing-admin.service.ts:460`), which is what makes the per-tenant scale-event figure real.
 * Screen 10 cannot filter: its table needs every DP's bucket from one call.
 */
export function billingSummaryPath(window: BillingWindow, dataPlaneId?: string): string {
  const params = new URLSearchParams({ startDate: window.startDate, endDate: window.endDate });
  if (dataPlaneId !== undefined) params.set('dataPlaneId', dataPlaneId);
  return `${BILLING_SUMMARY_PATH}?${params.toString()}`;
}

export function getBillingSummary(
  window: BillingWindow,
  dataPlaneId?: string,
): Promise<CostSummary> {
  return api.get<CostSummary>(billingSummaryPath(window, dataPlaneId));
}

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * R7 WS-6 §3 E87 — `GET /v1/admin/billing/scale-events`, screen 14's cost view on scaling.
 *
 * Producer `site-admin-api/src/modules/billing-admin/billing-admin.controller.ts:73-74`,
 * `cp:billing:read` · **site**, `/v1/admin` → site-admin-api (`httproute.yaml:213`).
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type { ScaleEventWithCost };

/** The base path. ⛔ Never requested bare — see {@link scaleEventsPath}. */
export const BILLING_SCALE_EVENTS_PATH = '/v1/admin/billing/scale-events';

/**
 * Build E87's path.
 *
 * # ⛔ SAME `startDate` / `endDate` CONTRACT AS THE SUMMARY, AND FOR THE SAME REASON
 *
 * This route binds the **same** `billingLedgerFiltersSchema` as `/summary`
 * (`billing-admin.controller.ts:84`), so `startDate` and `endDate` are REQUIRED here too and its
 * own `@ApiQuery({ required: false })` decorators say the opposite. See {@link BillingWindow}: the
 * schema is what runs. That is why this takes a {@link BillingWindow} rather than an options bag —
 * an unbounded call is unrepresentable.
 *
 * @param dataPlaneId ⭐ **Screen 14 SHOULD pass it, and that is the opposite of screen 10.**
 * `buildLedgerFilters` applies `dataPlaneId` to both bases (`billing-admin.service.ts:460`), so a
 * per-DP call is the only way to attribute autoscale cost to a row. ⛔ Unfiltered, the response is
 * a **fleet** total (`billing.api.ts`'s `dataPlaneId` docblock records the same asymmetry for
 * `scaleEventCost`), and rendering it on one DP's row attributes the whole fleet's spend to it.
 */
export function scaleEventsPath(
  window: BillingWindow,
  dataPlaneId?: string,
  limit?: number,
): string {
  const params = new URLSearchParams({ startDate: window.startDate, endDate: window.endDate });
  if (dataPlaneId !== undefined) params.set('dataPlaneId', dataPlaneId);
  if (limit !== undefined) params.set('limit', String(limit));
  return `${BILLING_SCALE_EVENTS_PATH}?${params.toString()}`;
}

/**
 * E87 — autoscale scale-up/down events with their cost delta.
 *
 * ⛔ **`costDelta` IS A SIGNED DECIMAL STRING. NO ARITHMETIC, NO SUMMING, NO `parseFloat`.**
 * `scaleEventWithCostSchema` (`contracts/src/billing.ts:368-388`) types it `billingDecimalString`,
 * and a scale-DOWN is negative. Render it as given, with its sign — the same rule
 * {@link getBillingSummary}'s `cost` carries, and the reason WS-1 §3.1 forbids client-side money
 * maths outright.
 *
 * ⚠️ The plan cites the field as `cost_delta`; on the contract it is **`costDelta`**.
 *
 * ⭐ **This is `cp:billing:read`, which is separately holdable from `cp:dp:read`** ⇒ the cost column
 * degrades on its own. ⛔ Not a page-level 403, and ⛔ not a zero: an operator who cannot see money
 * must see *"not yours"*, never *"$0 of autoscale spend"*.
 */
export function getScaleEvents(
  window: BillingWindow,
  dataPlaneId?: string,
  limit?: number,
): Promise<readonly ScaleEventWithCost[]> {
  return api.get<readonly ScaleEventWithCost[]>(scaleEventsPath(window, dataPlaneId, limit));
}

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * WS-6 06d — the billing WORKSPACE reads: ledger · trend · pricing-rates · apps ·
 * reconciliation. All five are `cp:billing:read` on **site-admin-api**
 * (`billing-admin.controller.ts:37-133`), `/v1/admin` → site-admin-api.
 *
 * ⛔ **EXTENDED, NEVER FORKED.** `getBillingSummary` and `getScaleEvents` above already have live
 * consumers (`FleetCapacityPane`, `TenantPane`); a second billing module would put two query
 * builders on one route prefix, which is the `/v1/admin/roles`-vs-`/v1/admin/permissions/roles`
 * class of error `tenants-api-paths.test.ts` exists to catch.
 *
 * ⛔ **THE WRITE IS NOT HERE.** `PUT /v1/metering/pricing-rates` is **platform-api**, so it lives in
 * `metering.api.ts`. This module is one service and one prefix; that is the whole convention.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export const BILLING_LEDGER_PATH = '/v1/admin/billing/ledger';
export const BILLING_TREND_PATH = '/v1/admin/billing/trend';
export const BILLING_PRICING_RATES_PATH = '/v1/admin/billing/pricing-rates';
export const BILLING_APPS_PATH = '/v1/admin/billing/apps';
export const BILLING_RECONCILIATION_PATH = '/v1/admin/billing/reconciliation';

export type {
  BillingAppOption,
  BillingLedgerPage,
  BillingTrendGroupBy,
  CostLedgerKind,
  CostLedgerLine,
  CostLedgerScope,
  CostReconciliationEntry,
  CostReconciliationGranularity,
  CostTrend,
  PricingRateEntry,
};

/**
 * The ledger / trend query.
 *
 * ⛔ `startDate`, `endDate` and `granularity` are ALWAYS sent — see {@link BillingWindow} for why the
 * first two are non-negotiable, and note `billingTrendQuerySchema` `.extend()`s the same filters so
 * the requirement survives onto `/trend`.
 *
 * ⛔⛔ **EVERY OTHER KEY IS OMITTED WHEN ABSENT, AND `URLSearchParams` IS THE TRAP.** `params.set(k,
 * undefined as never)` serialises the literal string `'undefined'`, and every billing schema is
 * `.strict()` — so `?dataPlaneId=undefined` is a **400**, not an ignored parameter. The omission has
 * to be a branch, not a value.
 */
export interface BillingQuery {
  readonly startDate: string;
  readonly endDate: string;
  readonly granularity: 'hourly' | 'daily';
  readonly scope?: CostLedgerScope;
  readonly dataPlaneId?: string;
  readonly orgId?: string;
  readonly appName?: string;
  readonly resourceType?: string;
  readonly kind?: CostLedgerKind;
  readonly cursor?: string;
  readonly limit?: number;
}

/**
 * Serialise a {@link BillingQuery}.
 *
 * ⛔⛔ **KEY ORDER IS PART OF THIS FUNCTION'S CONTRACT, NOT AN IMPLEMENTATION DETAIL.**
 * `billing-api-paths.test.ts` pins the literal string, and the order below is the old console's
 * `buildBillingQuery` order (`site-admin/platform/src/billing/api.ts:65-78`) — kept **so the two are
 * diffable during the cutover**. ⚠️ A reordering is invisible in every render test and visible only
 * in that one assertion; do not "tidy" it alphabetically.
 */
export function billingQueryString(
  q: BillingQuery,
  extra?: Readonly<Record<string, string>>,
): string {
  const params = new URLSearchParams();
  /* ⛔ One branch per optional key — see {@link BillingQuery} on the `'undefined'` trap. */
  if (q.scope !== undefined) params.set('scope', q.scope);
  if (q.dataPlaneId !== undefined) params.set('dataPlaneId', q.dataPlaneId);
  if (q.orgId !== undefined) params.set('orgId', q.orgId);
  if (q.appName !== undefined) params.set('appName', q.appName);
  if (q.resourceType !== undefined) params.set('resourceType', q.resourceType);
  if (q.kind !== undefined) params.set('kind', q.kind);
  params.set('startDate', q.startDate);
  params.set('endDate', q.endDate);
  params.set('granularity', q.granularity);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.cursor !== undefined) params.set('cursor', q.cursor);
  for (const [key, value] of Object.entries(extra ?? {})) params.set(key, value);
  return params.toString();
}

export function billingLedgerPath(q: BillingQuery): string {
  return `${BILLING_LEDGER_PATH}?${billingQueryString(q)}`;
}

export function billingTrendPath(q: BillingQuery, groupBy: BillingTrendGroupBy): string {
  return `${BILLING_TREND_PATH}?${billingQueryString(q, { groupBy })}`;
}

/**
 * ⛔ **THESE FIVE KEYS AND NO OTHERS.** `billingReconciliationQuerySchema` is `.strict()` and accepts
 * only `startDate`, `endDate`, `granularity`, `scope`, `dataPlaneId` — sending `limit`, `kind` or a
 * `granularity: 'daily'` is a **400**, not an ignored parameter. ⚠️ That is why this is its own type
 * rather than a `Pick<BillingQuery, …>`: a `Pick` stays assignable from a wider object, so the extra
 * key would typecheck and 400 at runtime.
 */
export interface ReconciliationQuery {
  readonly startDate: string;
  readonly endDate: string;
  readonly granularity: CostReconciliationGranularity;
  readonly scope?: string;
  readonly dataPlaneId?: string;
}

export function billingReconciliationPath(q: ReconciliationQuery): string {
  const params = new URLSearchParams();
  if (q.scope !== undefined) params.set('scope', q.scope);
  if (q.dataPlaneId !== undefined) params.set('dataPlaneId', q.dataPlaneId);
  params.set('startDate', q.startDate);
  params.set('endDate', q.endDate);
  params.set('granularity', q.granularity);
  return `${BILLING_RECONCILIATION_PATH}?${params.toString()}`;
}

/*
 * ⛔ **NO NORMALIZERS.** The old module's `normalizeLine`/`normalizeSummary`/`normalizeRate`/
 * `normalizeTrend`/`normalizeScaleEvent` (`billing/api.ts:30-121`) are deliberately NOT ported: they
 * coerce a contract-typed response into itself, and the one thing they genuinely guarded — a
 * non-numeric money string — is now handled honestly by `parseCents → null` (`billing-money.tsx`)
 * instead of being coerced to `'0'`. ⭐ Coercing a malformed figure to zero on a BILLING screen is
 * the defect, not the safety net.
 */

/**
 * The summary path for the billing WORKSPACE — the full six-filter query.
 *
 * ## ⛔⛔ TWO BUILDERS ON ONE ROUTE, AND THAT IS DELIBERATE
 *
 * {@link billingSummaryPath} sends **only** `startDate`/`endDate`/`dataPlaneId`, because screen 10
 * (`FleetCapacityPane`) and screen 11 (`TenantPane`) need every DP's bucket from ONE unfiltered call —
 * see its own `dataPlaneId` docblock for why filtering there would attribute a fleet total to one
 * tenant. ⛔ **Its signature must not change**: both panes key their `useApiResource` on its output and
 * `ws6-api-paths.test.ts` pins the literal string.
 *
 * The billing workspace's Ledger screen has six filters and needs its summary tiles to agree with the
 * table beneath them, so it needs the wide query. ⇒ a **second** builder rather than an optional
 * parameter on the first: an options bag that can express both would let screen 10 accidentally filter,
 * which is the defect the narrow one exists to make unrepresentable.
 */
export function billingSummaryQueryPath(q: BillingQuery): string {
  return `${BILLING_SUMMARY_PATH}?${billingQueryString(q)}`;
}

/**
 * The scale-events request of the Cost page's Scale events tab: the window, the two filters and
 * `limit`. These are the keys `billingScaleEventsQuerySchema` honours (R-023). The type has no
 * `granularity`: the route ignores it, and a console that sends it depends on a key the server
 * keeps only for a console one release behind.
 */
export interface ScaleEventsQuery {
  readonly startDate: string;
  readonly endDate: string;
  readonly dataPlaneId?: string;
  readonly appName?: string;
  readonly limit?: number;
}

/**
 * The scale-events path for the Cost page's Scale events tab.
 *
 * Same two-builders rule as {@link billingSummaryQueryPath}: {@link scaleEventsPath} cannot express
 * `appName`, and `FleetCapacityPane` keys on it, so it is left exactly as it is. The keys go in the
 * order of {@link billingQueryString}: `dataPlaneId`, `appName`, `startDate`, `endDate`, `limit`.
 * An absent filter writes no key. The output never holds `granularity`.
 */
export function scaleEventsQueryPath(q: ScaleEventsQuery): string {
  const params = new URLSearchParams();
  if (q.dataPlaneId !== undefined) params.set('dataPlaneId', q.dataPlaneId);
  if (q.appName !== undefined) params.set('appName', q.appName);
  params.set('startDate', q.startDate);
  params.set('endDate', q.endDate);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  return `${BILLING_SCALE_EVENTS_PATH}?${params.toString()}`;
}

export function getBillingSummaryQuery(q: BillingQuery): Promise<CostSummary> {
  return api.get<CostSummary>(billingSummaryQueryPath(q));
}

export function getScaleEventsQuery(q: ScaleEventsQuery): Promise<readonly ScaleEventWithCost[]> {
  return api.get<readonly ScaleEventWithCost[]>(scaleEventsQueryPath(q));
}

/*
 * ⚠️ **`billingSummaryQueryPath` + `getBillingSummaryQuery` ARE ABOVE, and they are not mine.** I
 * wrote a byte-equivalent pair here (`getBillingSummaryFiltered`) and removed it on discovering the
 * collision: a second writer had added the same §7.2 builder — plus `scaleEventsQueryPath`, which
 * 06d's Autoscale screen needs — between my read and my write. ⛔ Deleting MY duplicate rather than
 * theirs is the only safe direction: theirs is the superset, and a same-named export defined twice is
 * a **parse error**, not a merge conflict, so it fails loudly rather than silently picking one.
 * ⇒ 06d consumes `getBillingSummaryQuery` / `getScaleEventsQuery`. Reported to the lead.
 */

export function getBillingLedger(q: BillingQuery): Promise<BillingLedgerPage> {
  return api.get<BillingLedgerPage>(billingLedgerPath(q));
}

export function getBillingTrend(q: BillingQuery, groupBy: BillingTrendGroupBy): Promise<CostTrend> {
  return api.get<CostTrend>(billingTrendPath(q, groupBy));
}

/** ⛔ No query at all — the route binds no schema, so an added parameter would be a `.strict()` 400. */
export function getPricingRates(): Promise<readonly PricingRateEntry[]> {
  return api.get<readonly PricingRateEntry[]>(BILLING_PRICING_RATES_PATH);
}

export function getBillingApps(): Promise<readonly BillingAppOption[]> {
  return api.get<readonly BillingAppOption[]>(BILLING_APPS_PATH);
}

export function getReconciliation(
  q: ReconciliationQuery,
): Promise<readonly CostReconciliationEntry[]> {
  return api.get<readonly CostReconciliationEntry[]>(billingReconciliationPath(q));
}
